/**
 * SEO & Accessibility Utilities for Himalayan Pansar Store
 */

export function generateDefaultProductAltText(product, imageIndex = 0, lang = 'ur') {
  if (!product || typeof product !== 'object') {
    return lang === 'en'
      ? 'Himalayan Pansar Store - 100% Pure Organic Herbal Products'
      : 'ہمالین پنسار سٹور - 100% خالص قدرتی دیسی جڑی بوٹیاں و علاج';
  }

  const nameUrdu = (typeof product.nameUrdu === 'string' ? product.nameUrdu : String(product.nameUrdu || '')).trim();
  const nameEn = (typeof product.nameEn === 'string' ? product.nameEn : String(product.nameEn || '')).trim();

  let title = '';
  if (nameUrdu && nameEn) {
    title = `${nameUrdu} (${nameEn})`;
  } else {
    title = nameUrdu || nameEn || 'دیسی جڑی بوٹی';
  }

  const isEn = lang === 'en';
  const descriptor = isEn
    ? '100% Pure Natural Organic Herbal Medicine'
    : '100% خالص قدرتی دیسی جڑی بوٹی و ہربل علاج';
  const storeName = isEn ? 'Himalayan Pansar Store' : 'ہمالین پنسار سٹور';

  const baseAlt = `${title} - ${descriptor} | ${storeName}`;

  if (imageIndex > 0) {
    return isEn 
      ? `${baseAlt} (View #${imageIndex + 1})`
      : `${baseAlt} (تصویر نمبر #${imageIndex + 1})`;
  }

  return baseAlt;
}

export function getProductAltText(product, imageIndex = 0, lang = 'ur') {
  try {
    if (!product || typeof product !== 'object') {
      return generateDefaultProductAltText(null, imageIndex, lang);
    }

    const rawCustom = product.imageAlt || product.altText || '';
    const customAlt = typeof rawCustom === 'string' ? rawCustom.trim() : String(rawCustom || '').trim();

    if (customAlt) {
      if (imageIndex > 0) {
        return lang === 'en'
          ? `${customAlt} (View #${imageIndex + 1})`
          : `${customAlt} (تصویر #${imageIndex + 1})`;
      }
      return customAlt;
    }

    return generateDefaultProductAltText(product, imageIndex, lang);
  } catch (err) {
    return 'ہمالین پنسار سٹور - خالص قدرتی دیسی جڑی بوٹی';
  }
}

export function updateDocumentSeo(seoData = {}) {
  if (typeof document === 'undefined' || !seoData || typeof seoData !== 'object') return;

  try {
    const {
      title,
      description,
      image,
      url = window.location.href,
      type = 'website',
      productData = null
    } = seoData;

    if (title && typeof title === 'string') {
      document.title = title;
    }

    const setMeta = (attr, key, content) => {
      if (!content || typeof content !== 'string') return;
      try {
        let elem = document.querySelector(`meta[${attr}="${key}"]`);
        if (!elem) {
          elem = document.createElement('meta');
          elem.setAttribute(attr, key);
          document.head.appendChild(elem);
        }
        elem.setAttribute('content', content);
      } catch (e) {}
    };

    if (description) {
      setMeta('name', 'description', description);
      setMeta('property', 'og:description', description);
      setMeta('name', 'twitter:description', description);
    }

    if (title) {
      setMeta('property', 'og:title', title);
      setMeta('name', 'twitter:title', title);
      setMeta('property', 'og:site_name', 'Himalayan Pansar Store | ہمالین پنسار سٹور');
    }

    if (image && typeof image === 'string') {
      setMeta('property', 'og:image', image);
      setMeta('name', 'twitter:image', image);
      setMeta('name', 'twitter:card', 'summary_large_image');
    }

    if (url && typeof url === 'string') {
      setMeta('property', 'og:url', url.split('?')[0]); // Use clean URL without query parameters for canonical/OG
      
      // Update canonical link
      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', url.split('?')[0]);
    }

    if (type && typeof type === 'string') {
      setMeta('property', 'og:type', type);
    }

    // Dynamic Structured Data (Schema.org)
    let schemaScript = document.getElementById('seo-structured-data');
    if (!schemaScript) {
      schemaScript = document.createElement('script');
      schemaScript.setAttribute('type', 'application/ld+json');
      schemaScript.setAttribute('id', 'seo-structured-data');
      document.head.appendChild(schemaScript);
    }

    const schemas = [];

    // LocalBusiness Schema (Always present)
    schemas.push({
      "@context": "https://schema.org",
      "@type": "HealthAndBeautyBusiness",
      "name": "Himalayan Pansar Store (ہمالین پنسار سٹور)",
      "image": "https://shirazipansar.pk/images/logo.png",
      "description": "Buy pure herbs, authentic pansar items, natural oils, and traditional herbal medicines online. Fast home delivery in Islamabad and across Pakistan.",
      "@id": "https://shirazipansar.pk",
      "url": "https://shirazipansar.pk",
      "telephone": "+923470005578",
      "priceRange": "$$",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Shop #2, Al-Rehman Plaza, PWD Road",
        "addressLocality": "Islamabad",
        "postalCode": "44000",
        "addressCountry": "PK"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 33.5651,
        "longitude": 73.1563
      }
    });

    // Product Schema (If applicable)
    if (type === 'product' && productData) {
      const price = productData.variants && productData.variants.length > 0 
        ? productData.variants[0].price 
        : (productData.price || 0);

      schemas.push({
        "@context": "https://schema.org/",
        "@type": "Product",
        "name": productData.nameEn || productData.nameUrdu || title,
        "image": image || (productData.images ? productData.images[0] : ''),
        "description": description,
        "sku": productData.id || productData.slug,
        "brand": {
          "@type": "Brand",
          "name": "Himalayan Pansar Store"
        },
        "offers": {
          "@type": "Offer",
          "url": url.split('?')[0],
          "priceCurrency": "PKR",
          "price": price,
          "availability": productData.inStock === false ? "https://schema.org/OutOfStock" : "https://schema.org/InStock"
        }
      });
    }

    schemaScript.textContent = JSON.stringify(schemas);

  } catch (err) {
    console.warn('SEO injection error:', err);
  }
}
